'use client';

import { Check } from 'lucide-react';
import { cn } from '@/core/utils/cn';
import { usePageContent } from '@/core/i18n/content';
import { MARKET_FIT_CONTENT } from '@/core/i18n/content/target-market-fit.content';
import type { MarketFitContent } from '@/core/i18n/content/target-market-fit.content';
import { useTranslation } from '@/core/i18n';

type FitLevel = 'excellent' | 'strong' | 'good';

const FIT_COLORS: Record<FitLevel, { color: string; bg: string }> = {
  excellent: { color: 'text-hms-green', bg: 'bg-hms-green/10' },
  strong: { color: 'text-hms-teal', bg: 'bg-hms-teal/10' },
  good: { color: 'text-hms-gold', bg: 'bg-hms-gold/10' },
};

export function TargetMarketFitTable() {
  const { t } = useTranslation();
  const c: MarketFitContent = usePageContent(MARKET_FIT_CONTENT);

  return (
    <section id="features-market-fit" className="py-16 lg:py-20">
      <div className="mx-auto max-w-5xl px-4 sm:px-6">
        <div className="text-center mb-10">
          <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-hms-gold mb-3">
            {c.badge}
          </p>
          <h2 className="text-2xl sm:text-3xl font-bold text-hms-text tracking-tight">
            {c.title}
          </h2>
          <p className="mt-3 text-sm text-hms-textSub max-w-xl mx-auto">
            {c.subtitle}
          </p>
        </div>

        <div className="border border-hms-border rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table id="features-market-fit-table" className="w-full text-left">
              <thead>
                <tr className="bg-hms-surface border-b border-hms-border">
                  <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-hms-textDim">
                    {c.headers.segment}
                  </th>
                  <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-hms-textDim text-center">
                    {c.headers.fitLevel}
                  </th>
                  <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-hms-textDim hidden sm:table-cell">
                    {c.headers.capabilities}
                  </th>
                </tr>
              </thead>
              <tbody>
                {c.segments.map((row, i) => {
                  const fitColor = FIT_COLORS[row.fit];
                  const fitLabel = c.fitLabels[row.fit];
                  return (
                    <tr
                      key={i}
                      id={`features-market-fit-row-${i}`}
                      className={cn(
                        'border-b border-hms-border/50',
                        i % 2 === 0 ? 'bg-hms-card/30' : 'bg-transparent',
                      )}
                    >
                      <td className="px-4 py-3">
                        <span className="text-sm font-medium text-hms-text">{row.segment}</span>
                        <span className="block sm:hidden text-[11px] text-hms-textDim mt-1">{row.note}</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={cn('inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full', fitColor.bg, fitColor.color)}>
                          <Check className="w-3 h-3" />
                          {fitLabel}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-[12px] text-hms-textSub hidden sm:table-cell">
                        {row.note}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
}
